import React, { useState } from "react";
import {
  Type, Scissors, Replace, Eraser, AlignLeft, Hash, CaseSensitive,
  Plus, ChevronDown,
} from "lucide-react";

const SCOPES = [
  { key: "all", label: "All" },
  { key: "selected", label: "Selected" },
  { key: "files", label: "Files" },
  { key: "folders", label: "Folders" },
];

const CASES = [
  ["title", "Title Case"], ["lower", "lowercase"], ["upper", "UPPERCASE"],
  ["sentence", "Sentence case"], ["camel", "camelCase"], ["pascal", "PascalCase"],
  ["snake", "snake_case"], ["kebab", "kebab-case"],
];

const SEPS = ["_", "-", ".", " "];

function Section({ icon: Icon, title, children, testid }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border-b border-white/8">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`manual-section-${testid}`}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-white/5 transition-colors duration-200"
      >
        <Icon className="h-4 w-4 text-[#00E5FF]" />
        <span className="text-sm text-white font-medium flex-1">{title}</span>
        <ChevronDown className={`h-4 w-4 text-neutral-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-4 pb-4 space-y-2">{children}</div>}
    </div>
  );
}

const inputCls =
  "w-full bg-black/60 border border-white/15 rounded-lg px-2.5 py-1.5 text-xs text-white font-mono-ui outline-none focus:border-[#00E5FF]/50";
const btnCls =
  "w-full rounded-lg px-3 py-1.5 text-xs font-semibold bg-[#00E5FF] text-black hover:bg-[#00B3CC] transition-colors duration-200 flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed";
const chipCls = (active) =>
  `text-[11px] px-2 py-1 rounded-full border transition-colors duration-200 ${
    active ? "bg-[#00E5FF] text-black border-[#00E5FF]" : "border-white/15 text-neutral-300 hover:border-[#00E5FF]/50"
  }`;

export default function ManualPanel({ onAddStep, hasItems }) {
  const [scope, setScope] = useState("all");
  const withScope = (step) => onAddStep({ ...step, scope });

  const [prefix, setPrefix] = useState("");
  const [suffix, setSuffix] = useState("");
  const [find, setFind] = useState("");
  const [replaceWith, setReplaceWith] = useState("");
  const [frCase, setFrCase] = useState(false);
  const [frWord, setFrWord] = useState(false);
  const [frRegex, setFrRegex] = useState(false);
  const [removeTextV, setRemoveTextV] = useState("");
  const [charMode, setCharMode] = useState("first");
  const [charCount, setCharCount] = useState(1);
  const [collapse, setCollapse] = useState(true);
  const [sepFrom, setSepFrom] = useState(["_"]);
  const [sepTo, setSepTo] = useState(" ");
  const [numStart, setNumStart] = useState(1);
  const [numInc, setNumInc] = useState(1);
  const [numPad, setNumPad] = useState(2);
  const [numPos, setNumPos] = useState("begin");
  const [numSep, setNumSep] = useState(" ");
  const [perFolder, setPerFolder] = useState(true);

  const toggleSep = (s) =>
    setSepFrom((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  return (
    <div className="flex flex-col h-full overflow-y-auto bg-[#070707]/95" data-testid="manual-panel">
      <div className="px-4 py-3 border-b border-white/10 sticky top-0 bg-[#070707] z-10">
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Apply to</div>
        <div className="flex flex-wrap gap-1.5">
          {SCOPES.map((s) => (
            <button
              key={s.key}
              onClick={() => setScope(s.key)}
              data-testid={`manual-scope-${s.key}`}
              className={chipCls(scope === s.key)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <Section icon={Type} title="Prefix / Suffix" testid="affix">
        <div className="flex gap-2">
          <input value={prefix} onChange={(e) => setPrefix(e.target.value)} placeholder="prefix e.g. 2026_" className={inputCls} data-testid="manual-prefix-input" />
          <button disabled={!hasItems || !prefix} onClick={() => withScope({ type: "prefix", text: prefix })} data-testid="manual-prefix-apply" className="shrink-0 rounded-lg px-3 text-xs bg-white/10 text-white border border-white/20 hover:bg-white/20 transition-colors duration-200 disabled:opacity-40">Add</button>
        </div>
        <div className="flex gap-2">
          <input value={suffix} onChange={(e) => setSuffix(e.target.value)} placeholder="suffix e.g. _final" className={inputCls} data-testid="manual-suffix-input" />
          <button disabled={!hasItems || !suffix} onClick={() => withScope({ type: "suffix", text: suffix })} data-testid="manual-suffix-apply" className="shrink-0 rounded-lg px-3 text-xs bg-white/10 text-white border border-white/20 hover:bg-white/20 transition-colors duration-200 disabled:opacity-40">Add</button>
        </div>
      </Section>

      <Section icon={Replace} title="Find & Replace" testid="find">
        <input value={find} onChange={(e) => setFind(e.target.value)} placeholder="find" className={inputCls} data-testid="manual-find-input" />
        <input value={replaceWith} onChange={(e) => setReplaceWith(e.target.value)} placeholder="replace with" className={inputCls} data-testid="manual-replace-input" />
        <div className="flex flex-wrap gap-1.5">
          <button className={chipCls(frCase)} onClick={() => setFrCase((v) => !v)} data-testid="manual-fr-case">Aa</button>
          <button className={chipCls(frWord)} onClick={() => setFrWord((v) => !v)} data-testid="manual-fr-word">Whole word</button>
          <button className={chipCls(frRegex)} onClick={() => setFrRegex((v) => !v)} data-testid="manual-fr-regex">.* Regex</button>
        </div>
        <button disabled={!hasItems || !find} onClick={() => withScope({ type: "findReplace", find, replaceWith, caseSensitive: frCase, wholeWord: frWord, regex: frRegex })} className={btnCls} data-testid="manual-find-apply"><Plus className="h-3.5 w-3.5" />Apply</button>
      </Section>

      <Section icon={Eraser} title="Remove" testid="remove">
        <div className="flex gap-2">
          <input value={removeTextV} onChange={(e) => setRemoveTextV(e.target.value)} placeholder="text to remove" className={inputCls} data-testid="manual-removetext-input" />
          <button disabled={!hasItems || !removeTextV} onClick={() => withScope({ type: "removeText", text: removeTextV, caseSensitive: false })} data-testid="manual-removetext-apply" className="shrink-0 rounded-lg px-3 text-xs bg-white/10 text-white border border-white/20 hover:bg-white/20 transition-colors duration-200 disabled:opacity-40">Go</button>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1.5">
            <button className={chipCls(charMode === "first")} onClick={() => setCharMode("first")} data-testid="manual-char-first">First</button>
            <button className={chipCls(charMode === "last")} onClick={() => setCharMode("last")} data-testid="manual-char-last">Last</button>
          </div>
          <input type="number" min={0} value={charCount} onChange={(e) => setCharCount(e.target.value)} className={`${inputCls} w-16`} data-testid="manual-char-count" />
          <span className="text-xs text-neutral-500">chars</span>
          <button disabled={!hasItems} onClick={() => withScope({ type: "removeChars", mode: charMode, count: charCount })} data-testid="manual-char-apply" className="ml-auto shrink-0 rounded-lg px-3 py-1 text-xs bg-white/10 text-white border border-white/20 hover:bg-white/20 transition-colors duration-200 disabled:opacity-40">Go</button>
        </div>
      </Section>

      <Section icon={AlignLeft} title="Trim & Separators" testid="trim">
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-neutral-300">
            <input type="checkbox" checked={collapse} onChange={(e) => setCollapse(e.target.checked)} data-testid="manual-collapse" /> collapse spaces
          </label>
          <button disabled={!hasItems} onClick={() => withScope({ type: "trim", collapse })} data-testid="manual-trim-apply" className="ml-auto rounded-lg px-3 py-1 text-xs bg-white/10 text-white border border-white/20 hover:bg-white/20 transition-colors duration-200 disabled:opacity-40">Trim</button>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-neutral-500">from</span>
          {SEPS.map((s) => (
            <button key={s} className={chipCls(sepFrom.includes(s))} onClick={() => toggleSep(s)} data-testid={`manual-sep-${s === " " ? "space" : s}`}>{s === " " ? "␣" : s}</button>
          ))}
          <span className="text-xs text-neutral-500">to</span>
          <input value={sepTo} onChange={(e) => setSepTo(e.target.value)} className={`${inputCls} w-12`} data-testid="manual-sep-to" />
          <button disabled={!hasItems} onClick={() => withScope({ type: "replaceSeparators", from: sepFrom, to: sepTo })} data-testid="manual-sep-apply" className="rounded-lg px-3 py-1 text-xs bg-white/10 text-white border border-white/20 hover:bg-white/20 transition-colors duration-200 disabled:opacity-40">Go</button>
        </div>
      </Section>

      <Section icon={CaseSensitive} title="Case" testid="case">
        <div className="grid grid-cols-2 gap-1.5">
          {CASES.map(([k, label]) => (
            <button key={k} disabled={!hasItems} onClick={() => withScope({ type: "case", style: k })} data-testid={`manual-case-${k}`} className="text-[11px] px-2 py-1.5 rounded-lg border border-white/15 text-neutral-300 hover:border-[#00E5FF]/50 hover:text-[#00E5FF] transition-colors duration-200 disabled:opacity-40">{label}</button>
          ))}
        </div>
      </Section>

      <Section icon={Hash} title="Numbering" testid="numbering">
        <div className="grid grid-cols-3 gap-2">
          <label className="text-[10px] text-neutral-500">Start<input type="number" value={numStart} onChange={(e) => setNumStart(e.target.value)} className={inputCls} data-testid="manual-num-start" /></label>
          <label className="text-[10px] text-neutral-500">Step<input type="number" value={numInc} onChange={(e) => setNumInc(e.target.value)} className={inputCls} data-testid="manual-num-inc" /></label>
          <label className="text-[10px] text-neutral-500">Pad<input type="number" value={numPad} onChange={(e) => setNumPad(e.target.value)} className={inputCls} data-testid="manual-num-pad" /></label>
        </div>
        <div className="flex items-center gap-1.5">
          <button className={chipCls(numPos === "begin")} onClick={() => setNumPos("begin")} data-testid="manual-num-begin">Start</button>
          <button className={chipCls(numPos === "end")} onClick={() => setNumPos("end")} data-testid="manual-num-end">End</button>
          <span className="text-xs text-neutral-500 ml-1">sep</span>
          <input value={numSep} onChange={(e) => setNumSep(e.target.value)} className={`${inputCls} w-12`} data-testid="manual-num-sep" />
        </div>
        <label className="flex items-center gap-1.5 text-xs text-neutral-300">
          <input type="checkbox" checked={perFolder} onChange={(e) => setPerFolder(e.target.checked)} data-testid="manual-num-perfolder" /> restart numbering per folder
        </label>
        <button disabled={!hasItems} onClick={() => withScope({ type: "numbering", start: numStart, increment: numInc, padding: numPad, position: numPos, separator: numSep, perFolder })} className={btnCls} data-testid="manual-num-apply"><Plus className="h-3.5 w-3.5" />Add numbering</button>
      </Section>
    </div>
  );
}
